type MainRow = {
  year: string
  activity: string
  results: number
  frl: number
  paid: number | null
  payer: string | null
  available: number | null
}

type ProjectRow = {
  name: string
  proponent: string
  partner: string
  y2017: number | null
  y2018: number | null
  methodology: string
}

/* ===============================
   MAIN DATA
================================= */

const data: MainRow[] = [
  {
    year: "2015",
    activity:
      "Reducing emissions from deforestation, reducing emissions from forest degradation, enhancement of forest carbon stocks (REDD+)",
    results: 35245948,
    frl: 78953951,
    paid: null,
    payer: null,
    available: null
  },
  {
    year: "2016",
    activity: "REDD+",
    results: 35245948,
    frl: 78953951,
    paid: null,
    payer: null,
    available: null
  },
  {
    year: "2017",
    activity: "REDD+",
    results: 46337172,
    frl: 78953951,
    paid: 5915491,
    payer: "Multiple",
    available: 40421681
  },
  {
    year: "2018",
    activity: "REDD+",
    results: 46337172,
    frl: 78953951,
    paid: 4792505.9,
    payer: "Multiple",
    available: 41544666.1
  }
]

/* ===============================
   PROJECT DATA
================================= */

const projects: ProjectRow[] = [
  {
    name: "Reduced Emissions from Deforestation and Degradation in Keo Seima Wildlife Sanctuary",
    proponent: "Royal Government of Cambodia (RGC), Ministry of Environment",
    partner: "Wildlife Conservation Society (WCS)",
    y2017: 1804031,
    y2018: 439129,
    methodology: "VM0015"
  },
  {
    name: "Southern Cardamom REDD+ Project",
    proponent: "Royal Government of Cambodia (RGC), Ministry of Environment",
    partner: "Wildlife Alliance (WA)",
    y2017: 3982378,
    y2018: 3946277,
    methodology: "Verra VM0009 following CCB & VCS standards"
  },
  {
    name: "Prey Lang Wildlife Sanctuary - Stung Treng REDD+ project Phase 1",
    proponent: "Ministry of Environment (Cambodia) and Mitsui & Co., Ltd. (Japan)",
    partner: "Conservation International (CI)",
    y2017: null,
    y2018: 278017.90,
    methodology: "CM_KH_AM004 Version 1.1"
  },
  {
    name: "Tumring REDD+ Project",
    proponent: "The Royal Government of Cambodia, Forestry Administration",
    partner: "Forestry Administration",
    y2017: 129082,
    y2018: 129082,
    methodology: "VM0009"
  }
]

/* ===============================
   HELPERS
================================= */

const formatNumber = (value: number | null) =>
  value !== null
    ? value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    : "-"

const sum = (values: (number | null)[]) =>
  values.reduce((acc, val) => acc + (val ?? 0), 0)

/* ===============================
   COMPONENT
================================= */

export default function PagePrivacy() {
  const total2017 = sum(projects.map((p) => p.y2017))
  const total2018 = sum(projects.map((p) => p.y2018))

  return (
    <div className="container mx-auto max-w-7xl space-y-8 px-6 py-10">
      {/* Header */}
      <header className="space-y-3 border-b pb-4 mt-4">
        <h1 className="text-3xl font-bold tracking-tight">
          Information on Cambodia´s REDD+ Results
        </h1>
      </header>

      {/* ===============================
           MAIN TABLE
      ================================ */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold">
          Results period 2015-2018
        </h2>

        <div className="overflow-x-auto rounded-xl border shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
            <tr>
              <th className="border p-3 text-left">
                Year
              </th>
              <th className="border p-3 text-left">
                REDD+ ACTIVITIES
              </th>
              <th className="border p-3 text-left">
                RESULTS (tCO2 eq)
              </th>
              <th className="border p-3 text-left">
                ASSESSED FOREST REFERENCE LEVEL (tCO2 eq)
              </th>
              <th className="border p-3 text-left">
                VOLUME OF RESULTS FOR WHICH PAYMENTS WERE RECEIVED (tCO2 eq)
              </th>
              <th className="border p-3 text-left">
                ENTITY PAYING FOR RESULTS
              </th>
              <th className="border p-3 text-left">
                VOLUME OF RESULTS AVAILABLE FOR WHICH PAYMENTS HAVE NOT BEEN CLAIMED (tCO2 eq)
              </th>
            </tr>
            </thead>

            <tbody>
            {data.map((row) => (
              <tr key={row.year} className="hover:bg-gray-50">
                <th className="border p-3">
                  {row.year}
                </th>
                <td className="border p-3">
                  {row.activity}
                </td>
                <td className="border p-3">
                  {formatNumber(row.results)}
                </td>
                <td className="border p-3">
                  {formatNumber(row.frl)}
                </td>
                <td className="border p-3">
                  {formatNumber(row.paid)}
                </td>
                <td className="border p-3">
                  {row.payer ?? "-"}
                </td>
                <td className="border p-3">
                  {formatNumber(row.available)}
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-4 text-base leading-relaxed">
        <p>
          Cambodia possesses <strong>41,544,666.10 tCO 2 eq in available REDD+ results from 2018</strong>, as stipulated
          by the GCF REDD+ RBP
          Policy (approved at GCF Board 40)
        </p>
        <h2 className="text-xl font-semibold">Explanatory note</h2>
        <p>
          Explanatory note
          Based on the Technical Report on the Technical Analysis of the BUR REDD+ Technical Annex,
          Cambodia reported REDD+ results from implementing activities aimed at reducing emissions
          from deforestation and forest degradation, as well as enhancing forest carbon stocks, between
          2015 and 2018. These efforts resulted in a total reduction of 163,166,240 tonnes of carbon
          dioxide equivalent (tCO₂eq), which were compared to an assessed Forest Reference Level (FRL)
          of 78,953,951 tCO₂eq per year.
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>2015–2016:</strong> 35,245,948 tCO₂eq per year</li>
          <li><strong>2017–2018:</strong> 46,337,172 tCO₂eq per year</li>
        </ul>
        <p>
          During the period of 2017–2018, Cambodia received payments from multiple entities for reduced
          emissions from deforestation through three REDD+ projects. Two of these projects operated
          under the voluntary carbon market and adhered to VERRA standards and had received payments
          from, while the third was part of the Joint Crediting Mechanism (JCM).
        </p>
      </section>

      {/* ===============================
           PROJECT TABLE
      ================================ */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold">
          Volume of results for which Cambodia received payments from REDD+ Projects
        </h2>

        <div className="overflow-x-auto rounded-xl border shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
            <tr>
              <th rowSpan={2} className="border p-3 text-left">
                Project name
              </th>
              <th rowSpan={2} className="border p-3 text-left">
                Proponent/Participants
              </th>
              <th rowSpan={2} className="border p-3 text-left">
                Implementing partners
              </th>
              <th colSpan={2} className="border p-3 text-center">
                Volume of results for which payments were received (tCO2 eq)
              </th>
              <th rowSpan={2} className="border p-3 text-left">
                Methodology
              </th>
            </tr>
            <tr>
              <th className="border p-3 text-center">2017</th>
              <th className="border p-3 text-center">2018</th>
            </tr>
            </thead>

            <tbody>
            {projects.map((project) => (
              <tr key={project.name} className="hover:bg-gray-50">
                <td className="border p-3 align-top">
                  {project.name}
                </td>
                <td className="border p-3 align-top">
                  {project.proponent}
                </td>
                <td className="border p-3 align-top">
                  {project.partner}
                </td>
                <td className="border p-3 text-right tabular-nums">
                  {formatNumber(project.y2017)}
                </td>
                <td className="border p-3 text-right tabular-nums">
                  {formatNumber(project.y2018)}
                </td>
                <td className="border p-3">
                  {project.methodology}
                </td>
              </tr>
            ))}

            {/* Totals */}
            <tr className="bg-gray-100 font-semibold">
              <td className="border p-3">Total</td>
              <td className="border p-3"></td>
              <td className="border p-3"></td>
              <td className="border p-3 text-right tabular-nums">
                {formatNumber(total2017)}
              </td>
              <td className="border p-3 text-right tabular-nums">
                {formatNumber(total2018)}
              </td>
              <td className="border p-3"></td>
            </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Footer Note */}
      <p className="text-sm text-gray-600">
        The information on REDD+ Results for the period <strong>2017–2018</strong>, presented above, can also be accessed via the{" "}
        <a
          className="text-primary font-medium hover:underline"
          href="https://redd.unfccc.int/info-hub.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          Lima REDD+ Information Hub
        </a>.
      </p>
    </div>
  )
}
